import { useEffect, useState } from 'react';
import { subscriptionService } from '../lib/subscriptionService';
import { useAuth } from '../contexts/AuthContext';

const TestModeIndicator = () => {
  const [isTestMode, setIsTestMode] = useState(false);
  const [isPaywallEnabled, setIsPaywallEnabled] = useState(false);
  const { user } = useAuth();
  
  useEffect(() => {
    const checkTestMode = async () => {
      try {
        // Force cache refresh only once per session, not on every check
        const settings = await subscriptionService.getPaywallSettings(false);
        // Only log in development environment
        if (process.env.NODE_ENV !== 'production') {
          console.log('TestModeIndicator - Current settings:', settings);
        }
        setIsTestMode(settings.test_mode);
        setIsPaywallEnabled(settings.paywall_enabled);
      } catch (error) {
        console.error('Error checking test mode:', error);
      }
    };
    
    // Check immediately on mount
    checkTestMode();
    
    // Reduced check frequency to 60 seconds for better performance
    const interval = setInterval(checkTestMode, 60000);
    return () => clearInterval(interval);
  }, []);
  
  // Also check when the component becomes visible (tab focus)
  useEffect(() => {
    // Keep track of last check time to avoid too frequent checks
    let lastCheckTime = 0;
    const MIN_CHECK_INTERVAL = 30000; // 30 seconds minimum between checks
    
    const handleVisibilityChange = () => {
      const now = Date.now();
      // Only check if we haven't checked recently (within 30 seconds)
      if (document.visibilityState === 'visible' && now - lastCheckTime > MIN_CHECK_INTERVAL) {
        lastCheckTime = now;
        const checkTestMode = async () => {
          try {
            const settings = await subscriptionService.getPaywallSettings(false);
            // Only log in development environment
            if (process.env.NODE_ENV !== 'production') {
              console.log('TestModeIndicator - Visibility change settings:', settings);
            }
            setIsTestMode(settings.test_mode);
            setIsPaywallEnabled(settings.paywall_enabled);
          } catch (error) {
            console.error('Error checking test mode on visibility change:', error);
          }
        };
        checkTestMode();
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);
  
  // Only show the indicator to authenticated users and when in test mode
  if (!user || !isTestMode) {
    return null;
  }
  
  return (
    <div className={`fixed bottom-0 left-0 right-0 z-50 p-2 text-center text-sm font-medium ${isPaywallEnabled ? 'bg-amber-200 text-amber-900' : 'bg-blue-200 text-blue-900'}`}>
      {isPaywallEnabled ? (
        <>
          <span className="font-bold">⚠️ Test Mode Active</span>: Paywall is currently enabled but in test mode.
          {' '}<span className="underline">Users will be restricted based on subscription.</span>
        </>
      ) : (
        <>
          <span className="font-bold">🧪 Test Mode Active</span>: Paywall is currently disabled.
          {' '}<span className="underline">All users have full access regardless of subscription status.</span>
        </>
      )}
    </div>
  );
};

export default TestModeIndicator;
